// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import {
  CalendarTodayOutlined,
  EventAvailableOutlined,
  EventOutlined,
  EventRepeatOutlined,
} from '@/constants/hardfin-standard-icons';
import { ReactNode } from 'react';
import { z } from 'zod';

const PROJECT_STATUS_VALUES = [
  'PAST',
  'ACTIVE',
  'UPCOMING',
  'DATE_RANGE_INCOMPLETE',
] as const;

export type ProjectStatus = (typeof PROJECT_STATUS_VALUES)[number];

export const projectStatusSchema = z.enum(PROJECT_STATUS_VALUES);

const PROJECT_STATUS_SLUG_VALUES = [
  'past',
  'active',
  'upcoming',
  'date-range-incomplete',
] as const;

export type ProjectStatusSlug = (typeof PROJECT_STATUS_SLUG_VALUES)[number];

export const projectStatusSlugSchema = z.enum(PROJECT_STATUS_SLUG_VALUES);

export const PROJECT_STATUS_SLUGS = projectStatusSlugSchema.options;

const PROJECT_STATUS_BY_SLUG: Record<ProjectStatusSlug, ProjectStatus> = {
  past: 'PAST',
  active: 'ACTIVE',
  upcoming: 'UPCOMING',
  'date-range-incomplete': 'DATE_RANGE_INCOMPLETE',
};

/** Converts a filter slug to the status value the API uses. */
export function toProjectStatus(slug: ProjectStatusSlug): ProjectStatus {
  return PROJECT_STATUS_BY_SLUG[slug];
}

export const PROJECT_STATUS_LABELS: Record<ProjectStatus, string> = {
  PAST: 'Past',
  ACTIVE: 'Active',
  UPCOMING: 'Upcoming',
  DATE_RANGE_INCOMPLETE: 'No dates set',
};

/**
 * Icons for each project status, used in filter toggles and status displays.
 */
export const PROJECT_STATUS_ICONS: Record<ProjectStatus, ReactNode> = {
  UPCOMING: <EventOutlined fontSize="small" />,
  ACTIVE: <EventAvailableOutlined fontSize="small" />,
  PAST: <EventRepeatOutlined fontSize="small" />,
  DATE_RANGE_INCOMPLETE: <CalendarTodayOutlined fontSize="small" />,
};

/**
 * A convenience function that returns the label for a project status.
 *
 * @param status - The project status for which to get the label.
 */
export function getProjectStatusLabel(status: ProjectStatus): string {
  return PROJECT_STATUS_LABELS[status];
}
